import { Button } from '@eventbrite/eds-button';
import { TYPE_ERROR } from '@eventbrite/eds-notification';
import { sdkRequest } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import loadable from '@loadable/component';
import React, { useState } from 'react';
import {
    FollowOrganizerProps,
    GACodes,
    NotificationBarProps,
    RemindMeProps,
} from '../../types';
import { useNotificationsContext } from './../../contexts';
import './RemindMe.scss';

const UNREMIND_ACTION = 'unremind';
const REMIND_ACTION = 'remind';

const redirectToSignIn = (eventUrl: string) => {
    window.location.href = `/signin/confirmation/?forward=${eventUrl}`;
};

const setReminderApi = (eventId: string, action: string) => {
    return sdkRequest(`/destination/events/${eventId}/${action}/`, {
        method: 'POST',
    });
};

const trackAnalytics = (action: string, eventName: string) => {
    trackAnalyticsEvent({
        action: action,
        category: GACodes.GA_CATEGORY_LISTING,
        label: eventName,
    });
};
const LazyRemindMeModal = loadable(() => import('./RemindMeModal'));

export const RemindMe = (props: RemindMeProps & FollowOrganizerProps) => {
    const {
        eventId,
        eventUrl,
        eventName,
        isRemindedEvent,
        organizerId,
        gaSettings,
        gaCategory,
        organizers,
        isAuthenticated,
        userId,
        isGDPRCountry,
        organizerData,
    } = props;

    const [isReminded, setIsReminded] = useState(isRemindedEvent);
    const [opened, setOpened] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const { notificationState, setNotificationState } =
        useNotificationsContext();
    const { isHidden: isNotificationHidden } = notificationState;
    const notificationDefaultState: NotificationBarProps = {
        isHidden: true,
        type: TYPE_ERROR,
        hasCloseButton: true,
        children: isReminded
            ? gettext(
                  'We were unable to complete the unremind me request. Please try again.',
              )
            : gettext(
                  'We were unable to complete the remind me request. Please try again.',
              ),
    };

    const setReminder = () => {
        const action = isReminded ? UNREMIND_ACTION : REMIND_ACTION;
        const analyticsAction = isReminded
            ? GACodes?.GA_ACTION_UNREMINDME
            : GACodes?.GA_ACTION_REMINDME;

        trackAnalytics(analyticsAction, eventName);

        setIsFetching(true);

        setReminderApi(eventId, action)
            .then((response: { action_performed: boolean }) => {
                if (response.action_performed) {
                    if (!isReminded) {
                        setOpened(true);
                    }
                    setIsReminded(!isReminded);
                    !isNotificationHidden &&
                        setNotificationState({
                            ...notificationDefaultState,
                            isHidden: true,
                        });
                } else {
                    setNotificationState({
                        ...notificationDefaultState,
                        isHidden: false,
                    });
                }
            })
            .catch(() => {
                setNotificationState({
                    ...notificationDefaultState,
                    isHidden: false,
                });
            })
            .finally(() => setIsFetching(false));
    };

    const handleClick = () =>
        isAuthenticated ? setReminder() : redirectToSignIn(eventUrl);

    return (
        <>
            <Button
                style={isReminded ? 'neutral' : 'fill'}
                isLoading={isFetching}
                onClick={handleClick}
                size="block"
                __containerClassName="remind-me__button"
            >
                {isReminded ? gettext('Remove reminder') : gettext('Remind me')}
            </Button>

            {opened && (
                <LazyRemindMeModal
                    onClose={() => setOpened(false)}
                    opened={opened}
                    organizerId={organizerId}
                    gaSettings={gaSettings}
                    gaCategory={gaCategory}
                    organizers={organizers}
                    isAuthenticated={isAuthenticated}
                    userId={userId}
                    isGDPRCountry={isGDPRCountry}
                    organizerData={organizerData}
                />
            )}
        </>
    );
};

export default RemindMe;
